'use strict';
import DataTable from '../../../node_modules/datatables.net-dt/js/dataTables.dataTables.js';
import '../../../node_modules/datatables.net-dt/css/dataTables.dataTables.min.css';
import '../../../node_modules/datatables.net-responsive-dt/css/responsive.dataTables.css';
import '../../../node_modules/datatables.net-buttons-dt/js/buttons.dataTables.js';
import '../../../node_modules/datatables.net-buttons/js/buttons.colVis.js';
import '../../../node_modules/datatables.net-buttons/js/buttons.html5.js';
import '../../../node_modules/datatables.net-buttons/js/buttons.print.js';
import '../../../node_modules/datatables.net-responsive/js/dataTables.responsive.js';
import '../../../node_modules/datatables.net-responsive-dt/js/responsive.dataTables.js';
import '../../../node_modules/datatables.net-fixedcolumns/js/dataTables.fixedColumns.js';

import CSVGenerator from '../generators/CSVGenerator.js';
import XLSXGenerator from '../generators/XLSXGenerator.js';
import { DatatableHelper } from '../helpers/DatatableHelper.js';
import { Helper } from '../helpers/Helper.js';
import { DOM } from '../helpers/DOM.js';

export class Datatables {
	#dataTables;
	_choices = [];
	#datatableChanged;
	#datatables_per_page;
	#admin_ajax;
	#current_user_can = [];
	_levDatatables = {};
	#eventHandler = {};
	#eventAdded = false;
	static #columnToggleUserState = {};
	static #columnToggleDelegationBound = false;

	/** Avoid class selectors when table id contains `.` etc. */
	static #getLevitActionsHost( tableDomId ) {
		if ( typeof document === 'undefined' ) {
			return null;
		}
		const wrap = document.getElementById( `${ tableDomId }_wrapper` );
		return wrap?.querySelector( '.lev-datatable-levit-actions' ) ?? null;
	}

	static #applyLevitColumnToggleDom( input, checked ) {
		if ( ! input ) {
			return;
		}
		const row = input.closest( '.levcharity_checkbox_wrapper' );
		input.checked = checked;
		row?.classList.toggle( 'lev-column-toggle--checked', checked );
		if ( checked ) {
			input.setAttribute( 'checked', 'checked' );
		} else {
			input.removeAttribute( 'checked' );
		}
	}

	static instances = [];
	static datatableInstances = {};
	static instance = null;

	/**
	 * Show export in-progress state (Actions dropdown text → "Processing…").
	 * @param {HTMLElement|{ node: () => HTMLElement }|{ 0: HTMLElement }} container - Buttons container, e.g. dt.buttons().container()
	 */
	showExportLoader( container ) {
		const el = this.#getExportLoaderElement( container );
		if ( ! el ) return;
		const toggle = el.querySelector( '.export-actions-toggle' );
		if ( toggle ) {
			if ( ! toggle.dataset.originalExportHtml ) {
				toggle.dataset.originalExportHtml = toggle.innerHTML;
			}
			const svg = toggle.querySelector( 'svg' );
			toggle.innerHTML = wp.i18n.__( 'Processing…', 'levcharity' ) + ( svg ? ' ' + svg.outerHTML : '' );
		}
	}

	/**
	 * Hide loader: restore Actions dropdown text.
	 */
	hideExportLoader( container ) {
		const el = this.#getExportLoaderElement( container );
		if ( ! el ) return;
		const toggle = el.querySelector( '.export-actions-toggle' );
		if ( toggle && toggle.dataset.originalExportHtml ) {
			toggle.innerHTML = toggle.dataset.originalExportHtml;
			delete toggle.dataset.originalExportHtml;
		}
	}

	#getExportLoaderElement( container ) {
		if ( ! container ) return null;
		if ( container instanceof HTMLElement ) return container;
		if ( typeof container.node === 'function' ) return container.node();
		if ( container[ 0 ] && container[ 0 ] instanceof HTMLElement ) return container[ 0 ];
		if ( typeof container.get === 'function' && container.get( 0 ) ) return container.get( 0 );
		if ( container.nodeType === 1 ) return container;
		return null;
	}

	/**
	 * Resolve the visible export UI container for the loader (Actions dropdown or dt-buttons).
	 * Prefer .levcharity_datatable_export_actions so loader appears where the user clicked.
	 */
	#getExportButtonsContainerElement( dt, tableId ) {
		const tableWrapper = document.querySelector( `#${ tableId }_wrapper` );
		const datatableWrapper = tableWrapper?.closest( '.datatable_wrapper' );
		// Actions block is linked to table via data-table-id (set when dropdown is created)
		let actionsEl = document.querySelector( `.levcharity_datatable_export_actions[data-table-id="${ tableId }"]` );
		if ( ! actionsEl ) {
			actionsEl = datatableWrapper?.querySelector( '.levcharity_datatable_export_actions' );
		}
		if ( ! actionsEl && datatableWrapper?.parentElement ) {
			actionsEl = datatableWrapper.parentElement.querySelector( `.levcharity_datatable_export_actions[data-table-id="${ tableId }"]` ) ||
				datatableWrapper.parentElement.querySelector( '.levcharity_datatable_export_actions' );
		}
		const fromApi = this.#getExportLoaderElement( dt.buttons().container() );
		const fallbackDtButtons = document.querySelector( `#${ tableId }_wrapper .lev-datatable-exports .dt-buttons` ) ||
			document.querySelector( `#${ tableId }_wrapper .dt-buttons` );
		return actionsEl || fromApi || fallbackDtButtons;
	}

	constructor( table = null ) {
		if ( table ) {
			if ( table instanceof Element ) {
				this.#dataTables = [ table ];
			} else {
				this.#dataTables = table;
			}
		} else {
			this.#dataTables = document.querySelectorAll(
				'.dataTable:not(.initialized):not(.skip)'
			);
		}

		if (!window.LevDataTables) {
			window.LevDataTables = Datatables;
		}

		if ( typeof datatable_vars !== 'undefined' ) {
			this.#datatables_per_page = datatable_vars.datatable_per_page;
		}

		if ( typeof admin_ajax !== 'undefined' ) {
			this.#admin_ajax = admin_ajax[ 0 ];
		}

		if (typeof current_user_can !== 'undefined') {
			this.#current_user_can = current_user_can;
		}

		this._setCookie( 'dateFilterStart', '', 1 );
		this._setCookie( 'dateFilterEnd', '', 1 );
		this._setCookie( 'archivedDateFilterStart', '', 1 );
		this._setCookie( 'archivedDateFilterEnd', '', 1 );
		this._addLoadingClass();
		this.#initAllTables();
		this._datatableActions();
		this.addExistingFilters();
		this.addScrollForDropdown();

		Datatables.instances.push( this );
		Datatables.datatableInstances = {
			...Datatables.datatableInstances,
			...this._levDatatables,
		};


		if (!Datatables.instance) {
			this.handleDocumentClick = this.handleDocumentClick.bind(this);
			document.addEventListener('click', this.handleDocumentClick);
			Datatables.instance = this;
		}

		if ( ! Datatables.#columnToggleDelegationBound ) {
			Datatables.#columnToggleDelegationBound = true;
			document.addEventListener(
				'click',
				Datatables.#delegatedColumnToggleLabelClick,
				false
			);
		}
	}

	static #delegatedColumnToggleLabelClick = ( event ) => {
		const label = event.target.closest(
			'.columns-toggles-wrapper .column-name'
		);
		if ( ! label ) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		const wrapper = label.closest( '.levcharity_checkbox_wrapper' );
		if ( ! wrapper ) {
			return;
		}
		const checkbox = wrapper.querySelector( '.lev-datatable-column-toggle' );
		if ( ! checkbox ) {
			return;
		}
		const idx = Number.parseInt( checkbox.dataset.index, 10 );
		if ( Number.isNaN( idx ) ) {
			return;
		}
		const dtWrap =
			label.closest( '.dt-container' ) ||
			label.closest( '.dataTables_wrapper' );
		if ( ! dtWrap?.id?.endsWith( '_wrapper' ) ) {
			return;
		}
		const tableId = dtWrap.id.slice( 0, -'_wrapper'.length );
		const api = Datatables.datatableInstances?.[ tableId ];
		if ( ! api ) {
			return;
		}
		const state = Datatables.#columnToggleUserState[ tableId ];
		if (
			! state ||
			! Object.prototype.hasOwnProperty.call( state, idx )
		) {
			return;
		}
		const newVisible = ! state[ idx ];
		state[ idx ] = newVisible;
		try {
			api.column( idx ).visible( newVisible );
		} catch ( e ) {
			state[ idx ] = ! newVisible;
			return;
		}
		Datatables.#applyLevitColumnToggleDom( checkbox, newVisible );
	};

	#initTableResponseEvents(options, tableID) {
		let isImportCSV = false;
		let targets = [];

		options?.events.forEach((item) => {
			if (item?.csvImport === 'true') {
				isImportCSV = true;
			}

			if (item?.targets) {
				targets = item?.targets.split(', ') || [];
			}
		});

		if (isImportCSV) {
			targets.forEach((event) => {
				document.addEventListener(event, (event) => this.handleSuccessReload(event, tableID));
			});
		}
	}


	reRender( id = '' ) {
		if ( ! id ) {
			Object.keys( this._levDatatables ).forEach( ( key ) => {
				const instance = this._levDatatables[ key ];
				instance.draw();
				instance.ajax.reload();
			} );
		} else {
			const instance = this._levDatatables[ id ];
			if ( ! instance ) {
				this.initTable( document.querySelector( '#' + id ) );
			} else {
				instance.draw();
				instance.ajax.reload();
			}
		}
	}

	_setCookie( name, value, days ) {
		let expires = '';
		if ( days ) {
			const date = new Date();
			date.setTime( date.getTime() + days * 24 * 60 * 60 * 1000 );
			expires = '; expires=' + date.toUTCString();
		}
		document.cookie = name + '=' + value + expires + '; path=/';
	}

	_getCookie( name ) {
		const cookies = document.cookie.split( '; ' );
		for ( const cookie of cookies ) {
			const [ key, value ] = cookie.split( '=' );
			if ( key === name ) {
				return decodeURIComponent( value );
			}
		}
		return null;
	}

	_addLoadingClass() {
		DataTable.defaults.fnInitComplete = ( dTable ) => {
			dTable.nTableWrapper.classList.add( 'loaded' );
		};

		DataTable.ext.search.push( function ( settings, data, dataIndex ) {
			const dateFilterKey = settings.oInit.dateFilterKey;
			if ( ! dateFilterKey ) {
				return true;
			}

			if ( isNaN( dateFilterKey ) ) {
				const minElementHeading = document.querySelector(
					`[data-key="${ dateFilterKey }"][data-subtype="start"]`
				);
				const maxElementHeading = document.querySelector(
					`[data-key="${ dateFilterKey }"][data-subtype="end"]`
				);

				const minElement = document.querySelector(
					`#date-min-${ minElementHeading.dataset.index }`
				);
				const maxElement = document.querySelector(
					`#date-max-${ maxElementHeading.dataset.index }`
				);

				const start_date = Date.parse(
					data[ minElementHeading.dataset.index ]
				);
				const end_date = Date.parse(
					data[ maxElementHeading.dataset.index ]
				);
				const min = Date.parse( minElement.value );
				const max = Date.parse( maxElement.value );

				if (
					( isNaN( min ) && isNaN( max ) ) ||
					( isNaN( min ) && end_date <= max ) ||
					( min <= start_date && isNaN( max ) ) ||
					( min <= start_date && end_date <= max )
				) {
					return true;
				}
			} else {
				const minElement = document.getElementById(
					`date-min-${ dateFilterKey }`
				);
				const maxElement = document.getElementById(
					`date-max-${ dateFilterKey }`
				);

				if ( ! maxElement || ! minElement ) {
					return true;
				}

				const date = Date.parse( data[ dateFilterKey ] );
				const min = Date.parse( minElement.value );
				const max = Date.parse( maxElement.value );

				if (
					( isNaN( min ) && isNaN( max ) ) ||
					( isNaN( min ) && date <= max ) ||
					( min <= date && isNaN( max ) ) ||
					( min <= date && date <= max )
				) {
					return true;
				}
			}
			return false;
		} );
	}

	_attrToArray( columnNumbers ) {
		if ( columnNumbers ) {
			const columnsArray = columnNumbers.split( ',' );
			return columnsArray.map( ( item ) => parseInt( item ) );
		}
		return [];
	}


	_ajaxExport(table, action, type) {
		const url = typeof admin_ajax !== 'undefined' ? admin_ajax[0] : '';
		if (!url || !action) return;
		const params = { action: action, export_type: type };
		const formData = new FormData();
		for (const key in params) formData.append(key, params[key]);
		fetch(url, { method: 'POST', body: formData })
			.then(response => response.blob())
			.then(blob => {
				const a = document.createElement('a');
				a.href = window.URL.createObjectURL(blob);
				a.download = `export.${type === 'excel' ? 'xlsx' : 'csv'}`;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(a.href);
				a.remove();
			});
	}

	_appendAjaxDefaults(data, tableOptions) {
		if (
			! tableOptions?.ajax ||
			! tableOptions?.ajax?.data ||
			typeof tableOptions.ajax.data === 'function'
		) {
			return;
		}

		const ajaxData = tableOptions.ajax.data;

		Object.keys( ajaxData ).forEach( ( key ) => {
			if ( key === 'action' || typeof data[ key ] !== 'undefined' ) {
				return;
			}

			data[ key ] = ajaxData[ key ];
		} );
	}

	_getTableOptions( table ) {
		const headingsTags = table.querySelectorAll(
			'thead tr th,thead tr td'
		);
		const columnDefs = [];
		const columns = [];
		const events = [];
		const reportingDatatableWrapper = document.querySelector(
			'.reporting-datatable-wrapper'
		);
		let actionDom = '';
		if ( reportingDatatableWrapper ) {
			setTimeout( () => {
				const datatableWrapper = document.querySelector(
					'.lev-datatable--actions'
				);
				reportingDatatableWrapper.classList.remove('hidden')
				datatableWrapper.insertBefore(
					reportingDatatableWrapper,
					datatableWrapper.firstChild
				);
			}, 1000 );
			actionDom = `<"lev-datatable--actions"
					<"lev-datatable-search"f>
					<"lev-datatable-exports"B>
					<"${ table.id }--levit-actions lev-datatable-levit-actions">
				>
				<"levcharity_datatable_wrapper is-bordered-wrapper"t"
					<"lev-datatable--pagination-wrapper"lip>
				>`;
		} else {
			actionDom = `<"lev-datatable--actions"
					<"lev-datatable-search"f>
					<"${ table.id }--filters datatable-filters">
					<"lev-datatable-exports"B>
					<"${ table.id }--levit-actions lev-datatable-levit-actions">
				>
				<"levcharity_datatable_wrapper is-bordered-wrapper"t"
					<"lev-datatable--pagination-wrapper"lip>
				>`;
		}

		const searchableColumns = this._attrToArray( table.dataset.searchable );
		const hiddenColumns = this._attrToArray( table.dataset.hiddencolumns );
		const tableEvents = table.dataset.eventsresponsehandle;
		const csvImport = table.dataset.importcsv;
		let colWidths = table.dataset.colwidths;
		const childToggle = +table?.dataset?.childToggle;
		const childToggleVisibility =
			table?.dataset?.childToggleVisibility !== 'false';
		columnDefs.push( {
			targets: '_all', // Applies to all columns
			render: ( data, type, row, meta ) => {
				if ( type === 'filter' && typeof data === 'string' ) {
					if (
						data.includes( 'select_actions_wrapper' ) ||
						data.includes( 'dt_actions_wrapper' )
					) {
						return '';
					}
					if ( data.includes( '<' ) ) {
						const tmp = document.createElement( 'div' );
						tmp.innerHTML = data;
						return ( tmp.textContent || tmp.innerText || '' )
							.replace( /[\r\n\u2028]/g, '' )
							.trim();
					}
					return data;
				}

				const actions = '';
				if ( data?.child_row ) {
					data.child_row = data.child_row.map( ( _row ) => {
						const new_row = {};
						Object.keys( _row ).forEach( ( key ) => {
							let value = _row[ key ];

							if (
								typeof value === 'string' &&
								value?.includes( 'select_actions_wrapper' ) &&
								! value?.includes( 'dt_actions_wrapper' )
							) {
								value = this.#renderActions( value );
							}

							new_row[ key ] = value;
						} );

						return new_row;
					} );
				}

				if (
					typeof data === 'string' &&
					data?.includes( 'select_actions_wrapper' ) &&
					! data?.includes( 'dt_actions_wrapper' )
				) {
					data = this.#renderActions( data );
				}
				return data;
			},
		} );

		const emptyColumns = [];

		if ( childToggle >= 0 && childToggleVisibility ) {
			emptyColumns.push( childToggle );
		}

		const childRowToggleIconHtml =
			'<span class="details-controll-icon-wrapper"><svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 16.6622L20.6 12L22 13.4189L16 19.5L10 13.4189L11.4 12L16 16.6622Z" fill="#828282"/></svg></span>';

		headingsTags.forEach( ( el, index ) => {
			if ( index === childToggle ) {
				if ( childToggleVisibility ) {
					columns.push( {
						className: 'levcharity-datatables-details-control',
						orderable: false,
						data: null,
						type: 'html',
						defaultContent: '',
						render( _data, type, row ) {
							if ( type && type !== 'display' ) {
								return '';
							}
							const sub = row?.child_row;
							if (
								! Array.isArray( sub ) ||
								sub.length === 0
							) {
								return '';
							}
							return childRowToggleIconHtml;
						},
					} );
				} else {
					const leaveCount = emptyColumns
						.filter( ( el ) => index > el )
						.reduce( ( partialSum, a ) => partialSum + a + 1, 0 );
					columns.push( {
						data: index - leaveCount,
						type: 'html',
						className:
							'levcharity-datatables-details-control custom',
					} );
				}
			} else {
				const leaveCount = emptyColumns
					.filter( ( el ) => index > el )
					.reduce( ( partialSum, a ) => partialSum + a + 1, 0 );
				columns.push( {
					data: index - leaveCount,
					type: el.dataset.colType
						? el.dataset.colType
						: el.children.length
						? 'html'
						: 'string',
				} );
			}
		} );

		const noSorting = this._attrToArray( table.dataset.nosorting );
		if ( noSorting && noSorting.length ) {
			columnDefs.push( { orderable: false, targets: noSorting } );
		}

		if ( searchableColumns && searchableColumns.length ) {
			columnDefs.push( {
				searchable: true,
				targets: searchableColumns,
			} );
		}

		if(tableEvents){
			events.push({
				targets: tableEvents
			})
		}

		if(csvImport){
			events.push({
				csvImport : csvImport
			})
		}

		if ( hiddenColumns && hiddenColumns.length ) {
			columnDefs.push( { targets: hiddenColumns, visible: false } );
		}

		if ( colWidths && colWidths.length ) {
			const sizeUnitsRegex =
				/(?:px|%|vw|dvw|vh|dvh|em|rem|cm|mm|in|pt|pc|ex|ch|vmin|vmax|cap|ic|lh|rlh|vi|vb|avi|avb|adp|ads|adc|gs|gw|gh|gr|vwport|wh|slm|mozc|q|lq|dppx|dpcm|dpi|ms|s|Hz|kHz|deg|rad|grad|turn|dpi|dpcm|dppx|fr|c|rem)$/i;
			colWidths = JSON.parse( colWidths );

			colWidths.map( ( colWidth ) => {
				let targets = [];
				if ( Array.isArray( colWidth.targets ) ) {
					targets = colWidth.targets;
				} else {
					targets = [ colWidth.targets ];
				}

				colWidth.targets = targets;

				targets.forEach( ( targetIndex ) => {
					let width = colWidth.width;

					if ( ! sizeUnitsRegex.test( width ) ) {
						width += 'px';
					}

					colWidth.width = width;
				} );

				return colWidth;
			} );
		}

		const action = table.dataset.action ?? false,
			reporting = table?.dataset?.reporting === 'true',
			per_page = table.dataset.perpage
				? parseInt( table.dataset.perpage )
				: this.#datatables_per_page
				? this.#datatables_per_page
				: 10,
			id = table.dataset.id ?? false,
			email = table.dataset.email ?? false,
			type = table.dataset.type ?? false,
			queryParams = JSON.parse( table.dataset.query ?? '{}' ),
			currency = table.dataset.currencyfiltering ?? false,
			serverSide = table.dataset.serverside ?? false,
			processing = table.dataset.processing ?? true,
			time = new Date().getTime(),
			deferRender = table.dataset.deferrender ?? false,
			csv_export_columns = table?.dataset?.exportColumns || '',
			hide_csv_button = table?.dataset?.hideCsvButton === 'true',
			hide_search = table?.dataset?.hideSearch === 'true',
			sendUserTimezone = table?.dataset?.userTimezone === 'true',
			ajaxExport = table?.dataset?.ajaxExport === 'true',
			csv_button_text =
				( reporting ? '+ ' : '' ) +
				wp.i18n.__( 'Export .csv', 'levcharity' ),
			excel_button_text =
				( reporting ? '+ ' : '' ) +
				wp.i18n.__( 'Export .xlsx', 'levcharity' ),
			csv_export = table?.dataset?.csvExport || 'default',
			dom = actionDom,
			additionalActions = JSON.parse( table?.dataset?.actions || '[]' ),
			tableOptions = {
				processing,
				serverSide,
				searching: ! hide_search,
				dom,
				buttons: [],
				filter: true,
				pagingType: 'simple_numbers',
				language: {
					lengthMenu: `${ wp.i18n.__(
						'Show:',
						'levcharity'
					) } _MENU_`,
					searchPlaceholder: wp.i18n.__( 'Search', 'levcharity' ),
					info: `_PAGE_ ${ wp.i18n.__(
						'of',
						'levcharity'
					) } _PAGES_ (${
						/* translators: %s: total number of entries */ wp.i18n.sprintf(
							wp.i18n._n(
								'%s entry',
								'%s entries',
								`_TOTAL_`,
								'levcharity'
							),
							`_TOTAL_`
						)
					})`,
					zeroRecords: wp.i18n.__(
						'No matching records found',
						'levcharity'
					),
					infoEmpty: '',
					infoFiltered: '',
					paginate: {
						next: '<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_332_5260)"><path d="M8.59009 16.59L13.1701 12L8.59009 7.41L10.0001 6L16.0001 12L10.0001 18L8.59009 16.59Z" /> </g><defs><clipPath id="clip0_332_5260"><rect width="24" height="24"/></clipPath></defs></svg>',
						previous:
							'<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_332_5258)"><path d="M15.41 16.59L10.83 12L15.41 7.41L14 6L8 12L14 18L15.41 16.59Z" /></g><defs><clipPath id="clip0_332_5258"><rect width="24" height="24"/></clipPath></defs></svg>',
					},
					processing: `<div class="lds-spinner"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>`,
					loadingRecords: '',
				},
				oLanguage: {
					sSearch: '',
				},
				columnDefs,
				columns,
				colWidths,
				events,
				scrollX: false,
				headingsTags,
				additionalActions,
			};

		if ( ! hide_csv_button && ! serverSide && !ajaxExport ) {
			const exportOptions = {};

			const customize = ( csv ) => {
				const datatable = this._levDatatables[ table.id ];
				let data = datatable.rows().data().toArray();

				let actionsIndex = -1;

				data = data.map( ( row ) => {
					if ( typeof row.child_row !== 'undefined' ) {
						row[ 1 ] = {
							value: row[ 1 ],
							subitems: row.child_row,
						};

						delete row.child_row;
					}

					if ( ! Array.isArray( row ) ) {
						row = Object.values( row );
					}

					row = row
						.filter( ( d, i ) => {
							if ( d?.value ) {
								d = d.value;
							}
							const match =
								/class=".*select_actions_wrapper.*"/.test( d );

							if ( match ) {
								actionsIndex = i;
							}

							return ! match;
						} )
						.map( ( d ) => {
							if ( d?.value ) {
								d.value = d.value.replace( /<[^>]*>/g, '' );
							} else {
								d = ( d + '' ).replace( /<[^>]*>/g, '' );
							}
							return d;
						} );

					return row;
				} );

				const offset = ( childToggle >= 0 ) + 0;

				if ( typeof csv.split !== 'function' ) {
					const sheet = csv.xl.worksheets[ 'sheet1.xml' ];

					const export_cols = this._attrToArray( csv_export_columns );

					let headings = [ ...headingsTags ].map(
						( el, index ) => `
						<c t="inlineStr" s="2">
							<is>
								<t xml:space="preserve">${ el.textContent }</t>
							</is>
						</c>`
					);

					if ( csv_export_columns ) {
						headings = export_cols.map(
							( index ) => headings[ index ]
						);
					}

					if ( actionsIndex !== -1 ) {
						headings = headings.filter(
							( h, i ) => i !== actionsIndex
						);
					}

					const headingsString = headings.join( '' );
					sheet.querySelector( 'row[r="2"]' ).innerHTML =
						headingsString;

					sheet
						.querySelectorAll( 'row:not([r="1"]):not([r="2"])' )
						.forEach( ( r ) => r.remove() );

					const xlsxGenerator = new XLSXGenerator( '' );

					const xlsx = xlsxGenerator.generate( data, false, offset );

					sheet.querySelector( 'sheetData' ).innerHTML += xlsx;

					sheet
						.querySelectorAll( 'row:not([r="1"]):not([r="2"])' )
						.forEach( ( el, ind ) => {
							const row = ind + 3;
							el.setAttribute( 'r', row );
							el.querySelectorAll( 'c' ).forEach(
								( cell, cind ) => {
									cell.setAttribute(
										'r',
										xlsxGenerator.numberToChar( cind + 1 ) +
											row
									);
								}
							);
						} );
				} else {
					const newCsv = csv.split( '\n' );
					let headings = newCsv[ 0 ].split( ',' );
					const export_cols = this._attrToArray( csv_export_columns );

					headings = [ ...headingsTags ].map(
						( el ) => `"${ el.textContent }"`
					);

					if ( csv_export_columns ) {
						headings = export_cols.map(
							( index ) => headings[ index ]
						);
					}

					if ( actionsIndex !== -1 ) {
						headings = headings.filter(
							( h, i ) => i !== actionsIndex
						);
					}

					newCsv[ 0 ] = headings.join( ',' );

					csv = newCsv.join( '\n' );

					const csvGenerator = new CSVGenerator( '' );
					csv = `${ newCsv[ 0 ] }\n${ csvGenerator.generate(
						data,
						false,
						offset
					) }`;
				}

				return csv;
			};

			if ( csv_export_columns ) {
				exportOptions.columns = this._attrToArray( csv_export_columns );
			}

			const csvButton = {
				extend: 'csvHtml5',
				text: csv_button_text,
				actions: 'buttons-csv',
				className: 'levcharity_button medium download_button small',
				exportOptions,
				customize,
			};
			const excelButton = {
				extend: 'excelHtml5',
				text: excel_button_text,
				actions: 'buttons-excel',
				className: 'levcharity_button medium download_button small',
				exportOptions,
				customize,
			};

			tableOptions.buttons.push( csvButton, excelButton );
		}

		if ( (serverSide || ajaxExport)&& ! hide_csv_button ) {
			tableOptions.buttons.push(
				{
					extend: 'excelHtml5',
					text: 'Export to Excel',
					logic: 'callback',
					type: 'excel',
					action: ( e, dt, button, config ) => {
						const table_id = table.getAttribute( 'id' );
						if ( table_id ) {
							const exportContainer = this.#getExportButtonsContainerElement( dt, table_id );
							this.showExportLoader( exportContainer );

							const filters = document.querySelectorAll(
								`[data-for="${ table_id }"]`
							);
							const params = dt.ajax.params();
							const data = {
								action: `download_xlsx_${ action }`,
								params: params,
							};

							this._appendAjaxDefaults( data, tableOptions );

							Array.from( filters ).forEach( ( filter ) => {
								const filter_name =
									filter.getAttribute( 'name' );
								const filter_value = filter.value;
								data[ filter_name ] = filter_value;
							} );

							const defaultFilters = document.querySelector(`.server_side_exports_filters`);

							const formData = defaultFilters
								? Helper.objectToFormData(data, new FormData(defaultFilters))
								: Helper.objectToFormData(data);

							fetch( this.#admin_ajax, {
								method: 'POST',
								body: formData,
							} )
								.then( ( response ) => {
									if (!response.ok) {
										throw new Error('Network response was not ok');
									}
									return response;
								} )
								.then( ( response ) => {
									const disposition = response.headers.get(
										'Content-Disposition'
									);
									let filename = 'report.xlsx';
									if (
										disposition &&
										disposition.indexOf( 'attachment' ) !==
											-1
									) {
										const filenameRegex =
											/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
										const matches =
											filenameRegex.exec( disposition );
										if ( matches != null && matches[ 1 ] ) {
											filename = matches[ 1 ].replace(
												/['"]/g,
												''
											);
										}
									}
									return response.blob().then( ( blob ) => {
										const url =
											window.URL.createObjectURL( blob );
										const a = document.createElement( 'a' );
										a.href = url;
										a.download = filename;
										document.body.appendChild( a );
										a.click();
										window.URL.revokeObjectURL( url );
									} );
								} )
								.catch( ( error ) => {
									console.error( 'Fetch error:', error );
								} )
								.finally( () => {
									this.hideExportLoader( exportContainer );
								} );
						}
					},
					className: 'levcharity_button medium download_button',
				},
				{
					extend: 'csvHtml5',
					text: 'Export to CSV',
					logic: 'callback',
					type: 'csv',
					action: ( e, dt, button, config ) => {
						const table_id = table.getAttribute( 'id' );
						if ( table_id ) {
							const exportContainer = this.#getExportButtonsContainerElement( dt, table_id );
							this.showExportLoader( exportContainer );

							const filters = document.querySelectorAll(
								`[data-for="${ table_id }"]`
							);
							const params = dt.ajax.params();
							const data = {
								action: `download_csv_${ action }`,
								params: params,
							};

							this._appendAjaxDefaults( data, tableOptions );

							Array.from( filters ).forEach( ( filter ) => {
								const filter_name =
									filter.getAttribute( 'name' );
								const filter_value = filter.value;
								data[ filter_name ] = filter_value;
							} );
							const defaultFilters = document.querySelector(`.server_side_exports_filters`);

							const formData = defaultFilters
								? Helper.objectToFormData(data, new FormData(defaultFilters))
								: Helper.objectToFormData(data);

							fetch( this.#admin_ajax, {
								method: 'POST',
								body: formData,
							} )
								.then( ( response ) => {
									if (!response.ok) {
										throw new Error('Network response was not ok');
									}
									return response;
								} )
								.then( ( response ) => {
									const disposition = response.headers.get(
										'Content-Disposition'
									);
									let filename = 'report.csv';
									if (
										disposition &&
										disposition.indexOf( 'attachment' ) !==
											-1
									) {
										const filenameRegex =
											/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
										const matches =
											filenameRegex.exec( disposition );
										if ( matches != null && matches[ 1 ] ) {
											filename = matches[ 1 ].replace(
												/['"]/g,
												''
											);
										}
									}
									return response.blob().then( ( blob ) => {
										const url =
											window.URL.createObjectURL( blob );
										const a = document.createElement( 'a' );
										a.href = url;
										a.download = filename;
										document.body.appendChild( a );
										a.click();
										window.URL.revokeObjectURL( url );
									} );
								} )
								.catch( ( error ) => {
									console.error( 'Fetch error:', error );
								} )
								.finally( () => {
									this.hideExportLoader( exportContainer );
								} );
						}
					},
					className: 'levcharity_button medium download_button',
				}
			);
		}

		if ( action ) {
			tableOptions.ajax = {
				url: this.#admin_ajax ? this.#admin_ajax : 'admin-ajax.php',
				data: { action, id, email, type, time },
			};

			if ( currency ) {
				tableOptions.ajax.data.currency = currency;
			}

			if ( queryParams ) {
				tableOptions.ajax.data = Object.assign(
					{},
					tableOptions.ajax.data,
					queryParams
				);
			}

			if ( sendUserTimezone ) {
				tableOptions.ajax.data.timezone =
					Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || '';
			}
		}
		if ( per_page ) {
			tableOptions.pageLength = +per_page;
		}
		if ( deferRender ) {
			tableOptions.deferRender = true;
		}

		return tableOptions;
	}

	#renderActions( data ) {
		const options = [];

		const div = document.createElement( 'div' );
		div.innerHTML = data;
		document.body.append( div );
		const select = div?.children[ 0 ];

		[ ...select?.options ]?.forEach( ( option ) => {
			if ( option.value ) {
				options.push( {
					dataset: option.dataset,
					className: option.className,
					label: option.label,
					value: option.value,
					disabled: option?.getAttribute( 'disabled' ) !== null,
					hidden: option?.getAttribute( 'hidden' ) !== null,
				} );
			}
		} );

		data = `<div class="dt_actions_wrapper">
						<span class="actions-toggle-button lev-dt-dropdown-toggle">
							<svg xmlns="http://www.w3.org/2000/svg" width="4" height="16" viewBox="0 0 4 16" fill="none">
								<path d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14C0 13.45 0.195833 12.9792 0.5875 12.5875C0.979167 12.1958 1.45 12 2 12C2.55 12 3.02083 12.1958 3.4125 12.5875C3.80417 12.9792 4 13.45 4 14C4 14.55 3.80417 15.0208 3.4125 15.4125C3.02083 15.8042 2.55 16 2 16ZM2 10C1.45 10 0.979167 9.80417 0.5875 9.4125C0.195833 9.02083 0 8.55 0 8C0 7.45 0.195833 6.97917 0.5875 6.5875C0.979167 6.19583 1.45 6 2 6C2.55 6 3.02083 6.19583 3.4125 6.5875C3.80417 6.97917 4 7.45 4 8C4 8.55 3.80417 9.02083 3.4125 9.4125C3.02083 9.80417 2.55 10 2 10ZM2 4C1.45 4 0.979167 3.80417 0.5875 3.4125C0.195833 3.02083 0 2.55 0 2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0C2.55 0 3.02083 0.195833 3.4125 0.5875C3.80417 0.979167 4 1.45 4 2C4 2.55 3.80417 3.02083 3.4125 3.4125C3.02083 3.80417 2.55 4 2 4Z" fill="#828282"/>
							</svg>
						</span>
						<div class="actions-dropdown lev-dt-dropdown action-column column-dropdown" style="display: none; opacity: 0;">
							${ options
								.map( ( option ) => {
									let link = '';

									if ( option?.dataset?.url?.trim() ) {
										if (
											option.dataset?.newtab?.trim() ===
											'true'
										) {
											link = '_blank';
										} else {
											link = 'true';
										}
									}

									const dataAttrs = Object.keys(
										option.dataset
									)
										.map( ( key ) => {
											if ( link ) {
												if (
													key === 'url' ||
													key === 'newtab'
												) {
													return '';
												}
											}
											return `data-${ key
												.replace(
													/([a-z])([A-Z])/g,
													'$1-$2'
												)
												.toLowerCase() }="${ option.dataset[
												key
											].replace( /"/g, '&quot;' ) }"`;
										} )
										.join( ' ' );

									let attrs = '';

									if ( option.disabled ) {
										attrs += ' disabled';
									}

									if ( option.hidden ) {
										attrs += ' hidden';
									}

									let icon = '';
									let deleteClass = '';

									if (
										option.value.includes( 'delete' ) ||
										option.value.includes( 'remove' )
									) {
										icon =
											'<path d="M16 9v10H8V9zm-1.5-6h-5l-1 1H5v2h14V4h-3.5zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2z"></path>';
										deleteClass = 'action-delete';
									} else if (
										option.value.includes( 'edit' )
									) {
										icon =
											'<path d="m14.06 9.02.92.92L5.92 19H5v-.92zM17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z"></path>';
									} else if (
										option.value.includes( 'donations' ) ||
										option.value.includes( 'donors' ) ||
										option.value.includes( 'orders' )
									) {
										icon =
											'<path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4"></path>';
									} else if (
										option.value.includes( 'clone' ) ||
										option.value.includes( 'copy' ) ||
										option.value.includes( 'duplicate' )
									) {
										icon =
											'<path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2m0 16H8V7h11z"></path>';
									} else if (
										option.value.includes( 'pending' )
									) {
										icon =
											'<path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8m.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"></path>';
									} else if (
										option.value.includes( 'disapprove' ) ||
										option.value.includes( 'cancel' ) ||
										option.value.includes( 'draft' )
									) {
										icon =
											'<path d="M4.84 1.98 3.43 3.39l10.38 10.38-1.41 1.41-4.24-4.24-1.41 1.41 5.66 5.66 2.83-2.83 6.6 6.6 1.41-1.41zm13.21 10.38L23 7.4 21.57 6l-4.94 4.94zm-.71-4.96-1.41-1.41-2.12 2.12 1.41 1.41zM1.08 12.35l5.66 5.66 1.41-1.41-5.66-5.66z"></path>';
									} else if (
										option.value.includes( 'approve' ) ||
										option.value.includes( 'publish' ) ||
										option.value.includes( 'confirm' )
									) {
										icon =
											'<path d="m18 7-1.41-1.41-6.34 6.34 1.41 1.41zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12zM.41 13.41 6 19l1.41-1.41L1.83 12z"></path>';
									} else if (
										option.className.includes(
											'levcharity_modal'
										)
									) {
										icon =
											'<path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.89-2-2-2m0 14H5V8h14z"></path>';
									} else if ( link ) {
										if ( link === '_blank' ) {
											icon =
												'<path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3z"></path>';
										} else if (
											option.value === 'view-team-members'
										) {
											icon =
												'<path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3m-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3m0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5m8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5"></path>';
										} else {
											icon =
												'<path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1M8 13h8v-2H8zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5"></path>';
										}
									}

									if ( icon ) {
										icon = `<svg viewBox="0 0 24 24" width="16" height="16">${ icon }</svg>`;
									}

									if ( link ) {
										return `<a class="actions-option${
											option?.className
												? ' ' + option.className
												: ''
										} ${ deleteClass }" href="${
											option.dataset.url
										}"${
											link === '_blank'
												? ` target="_blank"`
												: ''
										} ${ dataAttrs }${ attrs }><span class="label">${
											option.label
										}</span>${ icon }</a>`;
									}
									return `<div class="actions-option${
										option?.className
											? ' ' + option.className
											: ''
									} ${ deleteClass }" ${ dataAttrs }${ attrs } data-value="${
										option.value
									}"><span class="label">${
										option.label
									}</span>${ icon }</div>`;
								} )
								.join( '' ) }
						</div>
						<div class="original_select" style="display: none; opacity: 0; overflow: hidden; height: 0;width: 0; visibility: hidden;position: absolute;left: -500vw;">${ data }</div>
					</div>`;

		div.remove();
		return data;
	}

	async _addDaterangeFilter( table, filtersWrapper, levDatatable ) {
		const daterangefilter = table.dataset.daterangefilter;
		const that = this;

		// Dynamically import the Lightpick library
		const { default: Lightpick } = await import(
			'lightpick'
		); /* webpackChunkName: "lightpick" */
		if ( daterangefilter ) {
			filtersWrapper.innerHTML += `
        <div class="dt-filter-wrapper filter-type-dropdown" id="order-date-filter-wrapper">
            <div class="dt-filter-field-wrapper">
                <div class="dt-filter-wrapper orderFilterWrapper form-field">
                    <input type="text" autocomplete="off" class="date-picker" placeholder="Dates" />
                </div>
            </div>
        </div>`;
		}

		setTimeout( () => {
			document.querySelectorAll( '.date-picker' ).forEach( ( field ) => {
				let dateFilterStartKey = 'dateFilterStart';
				let dateFilterEndKey = 'dateFilterEnd';

				if (field.id === 'order-archive-date-filter') {
					dateFilterStartKey = 'archivedDateFilterStart';
					dateFilterEndKey = 'archivedDateFilterEnd';
				}

				const onSelectCallback = ( start, end ) => {
					let dateFilterStart = start
							? start.format( 'YYYY-MM-DD' )
							: '',
						dateFilterEnd = end ? end.format( 'YYYY-MM-DD' ) : '';
					if ( isNaN( start?._i ) ) {
						dateFilterStart = '';
					}
					if ( isNaN( end?._i ) ) {
						dateFilterEnd = '';
					}

					if (
						dateFilterStart &&
						dateFilterEnd &&
						start._d.getTime() <= end._d.getTime()
					) {
						that._setCookie(
							dateFilterStartKey,
							dateFilterStart,
							1
						);
						that._setCookie( dateFilterEndKey, dateFilterEnd, 1 );
						that.#datatableChanged = true;
						const table_id = field
							.closest( '.dt-container' )
							.id.replace( '_wrapper', '' );
						const levDatatable = this._levDatatables[ table_id ];
						levDatatable.ajax.reload();
					} else if ( ! dateFilterEnd && ! dateFilterStart ) {
						setTimeout( () => {
							if (
								! that._getCookie( dateFilterStartKey ) &&
								! that._getCookie( dateFilterEndKey )
							) {
								return;
							}

							that._setCookie( dateFilterStartKey, '', 1 );
							that._setCookie( dateFilterEndKey, '', 1 );
							const table_id = field
								.closest( '.dt-container' )
								.id.replace( '_wrapper', '' );
							const levDatatable =
								this._levDatatables[ table_id ];
							levDatatable.ajax.reload();
						}, 10 );
					}
				};

				const lightpick = new Lightpick( {
					field,
					singleDate: false,
					selectForward: true,
					onSelect: onSelectCallback,
				} );

				const resetButton = document.createElement( 'button' );
				resetButton.classList.add( 'clear-button' );
				resetButton.innerHTML = '&times;';
				resetButton.dataset.levTooltip = wp.i18n.__(
					'Click to clear this filter',
					'levcharity'
				);
				field.parentElement.append( resetButton );
				field.parentElement.classList.add(
					'dt-daterange-filter-wrapper'
				);

				resetButton.addEventListener( 'click', () => {
					lightpick.setDateRange( null, null );
					field.value = '';
				} );

				field.addEventListener( 'input', ( e ) => {
					if ( e.target.value === '' ) {
						onSelectCallback( '', '' );
						lightpick.setDate( null );
					}
				} );
			} );
		}, 500 );
	}

	_hasFilters( tableHeadings, table ) {
		let hasFilters = false;
		tableHeadings.forEach( ( th, index ) => {
			if ( th.dataset.filter === '1' || table.dataset.daterangefilter ) {
				hasFilters = true;
			}
		} );

		if ( ! hasFilters ) {
			hasFilters = !! document.querySelector( '.lev-datatable--actions' );
		}

		return hasFilters;
	}

	initTable( table ) {
		if ( ! table ) {
			return;
		}

		const tableHeadings = table.querySelectorAll( 'thead th' );

		const tableOptions = this._getTableOptions( table );

		if(tableOptions){
			this.#initTableResponseEvents(tableOptions, table.id)
		}

		const classObject = this;

		let initComplete = () => {};

		if ( this._hasFilters( tableHeadings, table ) ) {
			const filtersWrapper = document.createElement( 'div' );
			filtersWrapper.className = 'filters-wrapper lev-dt-dropdown';
			filtersWrapper.style.opacity = 0;
			filtersWrapper.style.display = 'none';

			tableOptions.drawCallback = function () {
				if ( ! classObject.#datatableChanged ) {
					return;
				}

				classObject._updateDropdownFilters( this, tableHeadings );
				setTimeout( () => {
					classObject._updateChoices();
				}, 500 );
				classObject.#datatableChanged = false;
			};

			initComplete = function () {
				classObject._addDaterangeFilter(
					table,
					filtersWrapper,
					levDatatable
				);

				classObject.addExistingFilters( table, levDatatable );

				classObject._addFilters(
					this,
					tableHeadings,
					filtersWrapper,
					levDatatable,
					tableOptions
				);

				setTimeout( () => {
					const filters = document.querySelector(
						`.${ table.id }--filters`
					);
					if ( filters ) {
						filters.innerHTML += filtersWrapper.innerHTML;
					}
				}, 100 );

				setTimeout( () => {
					classObject.initChoices();
				}, 500 );
			};
		}

		tableOptions.initComplete = function () {
			initComplete.apply( this );
			const dtWrapper = this[ 0 ].closest( '.datatable_wrapper' );
			const tableID = this[ 0 ]?.id;

			if ( dtWrapper.dataset.export !== 'false' && classObject && classObject.#checkTablePermission(tableID) ) {
				let datatableActions = DOM.el(
					'.levcharity_page_actions_wrapper .levcharity_datatable_export_actions',
					dtWrapper.parentElement
				);

				if ( ! datatableActions ) {
					const options = classObject.#getFilteredOptions(tableID, tableOptions);

					datatableActions = document.createElement( 'div' );
					datatableActions.classList.add(
						'levcharity_datatable_export_actions'
					);
					datatableActions.setAttribute( 'data-table-id', table.id );
					if ( options.length ) {
						datatableActions.innerHTML = `<div class="export-actions-toggle lev-dt-dropdown-toggle">
								${ wp.i18n.__( 'Actions', 'levcharity' ) }
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none"><path d="M4 6.5L8 10.5L12 6.5" stroke="#828282" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
							</div>
							<div class="actions-dropdown lev-dt-dropdown" style="opacity: 0; display: none;" data-x="0">
								${ options
									.map( ( button, index ) => {
										return `<div class="action-option${
											button?.class
												? ' ' + button.class
												: ''
										}" data-index="${ index }" ${
											button?.attrs ? button.attrs : ''
										}>${ button.text }</div>`;
									} )
									.join( '' ) }
							</div>`;
					}

					let actionsWrapper =
						dtWrapper.parentElement.querySelector(
							'.levcharity_page_actions_wrapper'
						) || dtWrapper.previousElementSibling;

					if ( ! actionsWrapper ) {
						actionsWrapper = document.createElement( 'section' );
						actionsWrapper.classList.add(
							'levcharity_page_actions_wrapper'
						);

						DOM.insertBefore( dtWrapper, actionsWrapper );
					}

					if ( actionsWrapper ) {
						const addNew = DOM.el(
							'a.levcharity_button',
							actionsWrapper
						);
						if ( addNew ) {
							actionsWrapper.insertBefore(
								datatableActions,
								addNew
							);
						} else {
							actionsWrapper.append( datatableActions );
						}
					}

					datatableActions
						.querySelectorAll( '.action-option' )
						.forEach( ( option ) => {
							option.addEventListener( 'click', function () {
								const buttonIndex =
									this.getAttribute( 'data-index' );
								const button =
									tableOptions.buttons[ buttonIndex ];
								if (!button) return;

								if (
									button.extend === 'csvHtml5' ||
									button.extend === 'excelHtml5'
								) {
									let exportsButton = false;
									if(button?.logic == 'callback'){
										exportsButton =
											document.querySelector(`#${table.id}_wrapper .lev-datatable-exports .dt-buttons .buttons-${button.type}`);
									}else{
										exportsButton =
											document.querySelector(
												`#${
													table.id
												}_wrapper .${ button.actions
													?.split( ' ' )
													?.join( '.' ) }`
											);
									}
									if ( exportsButton ) {
										exportsButton.click();
									}
								}
							} );
						} );
				}
			}

			setTimeout( () => {
				let columnsToggleList = `<div class="lev-columns-toggle-list">`;
				const hiddenTargetsRaw = tableOptions.columnDefs.find(
					( el ) => el.visible === false
				)?.targets;
				const hiddenColumns =
					hiddenTargetsRaw === undefined || hiddenTargetsRaw === null
						? []
						: Array.isArray( hiddenTargetsRaw )
							? hiddenTargetsRaw
							: [ hiddenTargetsRaw ];
				classObject._seedColumnToggleUserState(
					table.id,
					tableOptions,
					hiddenColumns
				);
				tableOptions.columns.forEach( ( column, index ) => {
					const uid = Helper.uniqueID( 12 );
					const headingEl = tableOptions.headingsTags[ index ];

					if (
						column?.className ===
							'levcharity-datatables-details-control' ||
						headingEl.textContent === 'Actions' ||
						headingEl.textContent === '' ||
						headingEl.querySelector( '[id^="bulk_action_all_"]' ) ||
						headingEl.dataset?.skipColumnToggle === 'true'
					) {
						return;
					}

					columnsToggleList += `
								<div class="form-group levcharity_checkbox_wrapper ">
									<div class="form-field heading_only">
										<input type="checkbox" id="toggle_${ uid }" ${
											! hiddenColumns.some(
												( t ) => Number( t ) === index
											)
												? 'checked'
												: ''
										} class="lev-datatable-column-toggle" data-index="${ index }">
										<label class="column-name" for="toggle_${ uid }">${
											tableOptions.headingsTags[ index ]
												.textContent
										}</label>
									</div>
								</div>
							`;
				} );
				columnsToggleList += '</div>';




				const levitActionsHost = Datatables.#getLevitActionsHost(
					table.id
				);
				if ( levitActionsHost ) {
					levitActionsHost.innerHTML = `
							<div class="columns-toggles-wrapper action-wrapper">
								<span class="action-button lev-dt-dropdown-toggle table-column">
									<svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M0 13.8333V2.16667C0 1.70833 0.163194 1.31597 0.489583 0.989583C0.815972 0.663194 1.20833 0.5 1.66667 0.5H13.3333C13.7917 0.5 14.184 0.663194 14.5104 0.989583C14.8368 1.31597 15 1.70833 15 2.16667V13.8333C15 14.2917 14.8368 14.684 14.5104 15.0104C14.184 15.3368 13.7917 15.5 13.3333 15.5H1.66667C1.20833 15.5 0.815972 15.3368 0.489583 15.0104C0.163194 14.684 0 14.2917 0 13.8333ZM1.66667 5.5H13.3333V2.16667H1.66667V5.5ZM6.10417 9.66667H8.89583V7.16667H6.10417V9.66667ZM6.10417 13.8333H8.89583V11.3333H6.10417V13.8333ZM1.66667 9.66667H4.4375V7.16667H1.66667V9.66667ZM10.5625 9.66667H13.3333V7.16667H10.5625V9.66667ZM1.66667 13.8333H4.4375V11.3333H1.66667V13.8333ZM10.5625 13.8333H13.3333V11.3333H10.5625V13.8333Z" fill="#828282"/>
									</svg>
								</span>
								<div class="list-wrapper lev-dt-dropdown" style="display: none; opacity: 0;">
									${ columnsToggleList }
								</div>
							</div>
							<div class="full-view action-wrapper">
								<span class="action-button">
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#828282">
										<path d="M7 14H5v5h5v-2H7zm-2-4h2V7h3V5H5zm12 7h-3v2h5v-5h-2zM14 5v2h3v3h2V5z" />
									</svg>
								</span>
							</div>`;
				}
			}, 100 );

			classObject?._addEvents( levDatatable );
		};

		let isRendered = false;
		const tableId = table.id;
		const levDatatable = new DataTable( `#${ tableId }`, tableOptions );
        window?.LCE?.hooks?.editor && LCE?.hooks?.editor?.doAction('datatable_initialized', levDatatable)
        window?.LCE?.hooks?.editor && LCE?.hooks?.editor?.doAction(`datatable_initialized_${tableId}`, levDatatable)
		this._levDatatables[ tableId ] = levDatatable;
		this.hideShowpaginationWrapper( levDatatable );

		levDatatable.on( 'draw.dt', () => {
			this.hideShowpaginationWrapper( levDatatable );
			setTimeout( () => {
				try {
					levDatatable
						.containers()[ 0 ]
						?.querySelectorAll( 'colgroup' )
						?.forEach( ( row ) => {
							const cols = row.querySelectorAll( 'col' );

							let actionsColumnIndex = -1;
							if ( tableOptions.headingsTags ) {
								tableOptions.headingsTags.forEach( ( heading, index ) => {
									if ( heading.textContent === 'Actions' ) {
										actionsColumnIndex = index;
									}
								} );
							}

							let actionsWidthSet = false;
							if ( tableOptions.colWidths && actionsColumnIndex >= 0 ) {
								tableOptions.colWidths.forEach( ( col ) => {
									if ( Array.isArray( col.targets ) ) {
										if ( col.targets.includes( actionsColumnIndex ) ) {
											actionsWidthSet = true;
										}
									} else if ( col.targets === actionsColumnIndex ) {
										actionsWidthSet = true;
									}
								} );
							}

							tableOptions.colWidths?.forEach( ( col ) => {
								col.targets.forEach( ( index ) => {
									const colEl = cols[ index ];
									if ( colEl ) {
										colEl.style.width = col.width;
										colEl.style.setProperty(
											'--column-width',
											col.width
										);
									}
								} );
							} );

							if ( actionsColumnIndex >= 0 && ! actionsWidthSet && cols[ actionsColumnIndex ] ) {
								const defaultActionsWidth = '80px';
								cols[ actionsColumnIndex ].style.width = defaultActionsWidth;
								cols[ actionsColumnIndex ].style.setProperty(
									'--column-width',
									defaultActionsWidth
								);
							}
						} );

					const scrollingTable = levDatatable
						.containers()?.[ 0 ]
						?.querySelector( '.dt-scroll-body' );

					scrollingTable?.addEventListener( 'scroll', function () {
						const shown_dialog = document.querySelector(
							'dialog.actions-dropdown.shown'
						);
						if ( shown_dialog ) {
							document.querySelector( 'body' ).click();
						}
					} );

					const bodyCols = levDatatable
						.containers()[ 0 ]
						?.querySelector( '.dt-scroll-body .colgroup' )?.children;
					const headCols = levDatatable
						.containers()[ 0 ]
						?.querySelector( '.dt-scroll-head .colgroup' )?.children;
					if ( bodyCols && headCols ) {
						levDatatable
							.containers()[ 0 ]
							.querySelector( '.dt-scroll-head .colgroup' )
							.children.forEach( ( col, index ) => {
								const bodyCol = bodyCols[ index ];
								if ( bodyCol ) {
									col.style.width = bodyCol.style.width;
								}
							} );

						classObject._updateExistingFilters(
							table,
							tableOptions,
							tableHeadings
						);
					}
				} catch ( err ) {
					if ( typeof console !== 'undefined' && console.error ) {
						console.error( 'LevCharity Datatables draw.dt', err );
					}
				} finally {
					classObject._syncLevitColumnToggleCheckboxes( tableId );
				}
			}, 10 );

			if ( ! isRendered ) {
				setTimeout( () => {
					const filterElement = document.querySelector(
						`#${ tableId } [data-defaultfilter]`
					);
					if ( filterElement ) {
						const filterValue =
							filterElement.getAttribute( 'data-defaultfilter' );
						levDatatable
							.column( 7 )
							.search( `^(?!.*${ filterValue }).*$`, true, false )
							.draw();
						isRendered = true;
					}
				}, 100 );
			}

			setTimeout(async () => {
				const { default: Choices } = await import(/* webpackChunkName: "choices" */ 'choices.js');
				Array.from(document.querySelectorAll('.dt-choices-select'))
					.forEach(select => {
						if (select.classList.contains('choices__input')) return;
						new Choices(select, {
							shouldSortItems: false,
							allowHTML: true,
						})
					})
			}, 10)
		} );

		levDatatable.on( 'column-visibility.dt', () => {
			classObject._syncLevitColumnToggleCheckboxes( tableId );
		} );

		levDatatable.on(
			'click',
			'td.levcharity-datatables-details-control',
			( e ) => {
				const tr = e.target.closest( 'tr' );
				const row = levDatatable.row( tr );
				const rowData = row.data();
				const sub = rowData?.child_row;
				if (
					! Array.isArray( sub ) ||
					sub.length === 0
				) {
					return;
				}

				if ( row.child.isShown() ) {
					row.child.hide();
				} else {
					row.child( this.#renderChildRow( rowData ) ).show();
				}
			}
		);

		table.classList.add( 'initialized' );
	}

	#initAllTables() {
		this.#dataTables?.forEach( ( table ) => {
			this.initTable( table );
		} );
	}

	_updateDropdownFilters( that, tableHeadings ) {
		const filterableColumns = {},
			filterableColumnNames = {},
			filterableColumnsContent = {},
			filterableColumnsDefault = {},
			filterableIndexes = [];
		tableHeadings.forEach( ( th, index ) => {
			if ( th.dataset.filter === '1' ) {
				filterableIndexes.push( index );
				filterableColumns[ index ] = th.dataset.type ?? 'basic';
				filterableColumnsContent[ index ] = th.dataset.content ?? '';
				filterableColumnsDefault[ index ] =
					th.dataset.defaultfilter ?? '';
				if ( th.dataset.filtername ) {
					filterableColumnNames[ index ] = th.dataset.filtername;
				}
			}
		} );
		const filterTextTesting = document.createElement( 'div' );
		filterTextTesting.id = 'filterTextTesting';
		filterableIndexes.forEach( ( colIndex ) => {
			let filterField,
				filterName = tableHeadings[ colIndex ].innerText;

			if (
				tableHeadings[ colIndex ].dataset.subtype &&
				( tableHeadings[ colIndex ].dataset.subtype === 'start' ||
					tableHeadings[ colIndex ].dataset.subtype === 'end' )
			) {
				filterName = wp.i18n.__( 'date', 'levcharity' );
			}

			if (
				filterableColumnNames[ colIndex ] &&
				filterableColumnNames[ colIndex ] !== ''
			) {
				filterName = filterableColumnNames[ colIndex ];
			}

		if (
			filterableColumns[ colIndex ] === 'dropdown' ||
			filterableColumns[ colIndex ] === 'split-dropdown'
		) {
			// If the column has static data-options, don't overwrite with current page data.
			if ( tableHeadings[ colIndex ]?.dataset?.options ) {
				return;
			}

			const isSplit =
				filterableColumns[ colIndex ] === 'split-dropdown';

			filterField = '';
			let columnData = that.api().column( colIndex ).data(),
					filteredColumnData = [];
				for ( let i = 0; i < columnData.length; i++ ) {
					let val = columnData[ i ];
					if ( val && val.indexOf( '<' ) === 0 ) {
						filterTextTesting.innerHTML = val;
						val = filterTextTesting.innerText;
					}
					if ( ! ( filteredColumnData.indexOf( val ) >= 0 ) ) {
						filteredColumnData.push( val );
					}
				}
				filteredColumnData = filteredColumnData.sort(
					function ( a, b ) {
						return new Date( b ) - new Date( a );
					}
				);

				filterField += `<option value="">${ filterName }</option>`;

				let optionsArray = [];

				filteredColumnData.forEach( ( val ) => {
					if ( isSplit ) {
						val.split( ', ' ).forEach( ( v ) => {
							optionsArray.push( v );
						} );
					} else {
						optionsArray.push( val );
					}
				} );

				optionsArray = optionsArray.filter(
					( value, index, array ) =>
						value !== '' && array.indexOf( value ) === index
				);

				optionsArray.forEach( ( val ) => {
					filterField += `<option value="${ val }">${ val }</option>`;
				} );

				const existingFilter = document.getElementById(
					`${ filterName }-${ colIndex }`
				);
				if ( existingFilter ) {
					existingFilter.innerHTML = filterField;
				}
			}
		} );
	}

	async _updateExistingFilters( table, tableOptions, tableHeadings ) {
		const table_id = table.id;
		const action = table.dataset.action;

		const { Helper } = await import(
			'../helpers/Helper.js' /* webpackChunkName: "helper" */
		);

		const existingFiltersElements = document.querySelectorAll(
			`[data-for="${ table_id }"]`
		);
		if ( existingFiltersElements.length ) {
			const filters = [];
			const selectedFilters = {};
			existingFiltersElements.forEach( ( element ) => {
				const elementTagName = element.tagName.toLowerCase();
				if ( elementTagName === 'select' ) {
					filters.push( element.name );
				}

				selectedFilters[ element.name ] = element.value;
			} );

			await Helper.request(
				this.#admin_ajax,
				{
					action: 'update_datatable_filters_' + action,
					filters,
					...selectedFilters,
				},
				{
					method: 'get',
				}
			).then( ( response ) => {
				if ( response.values ) {
					const values = response.values;

					existingFiltersElements.forEach( ( element ) => {
						const elementTagName = element.tagName.toLowerCase();
						if ( elementTagName === 'select' ) {
							if (
								typeof values[ element.name ] !== 'undefined'
							) {
								const placeholder = [ ...element.options ].find(
									( o ) => o.value === ''
								);

								let newContent = '';

								if ( placeholder ) {
									newContent += placeholder.outerHTML;
								}

								for ( const key in values[ element.name ] ) {
									const val = values[ element.name ][ key ];
									newContent += `<option value="${ key }">${ val }</option>`;
								}

								element.innerHTML = newContent;
								element.value = selectedFilters[ element.name ];
							}
						}
					} );
				}
			} );
		}
	}

	async _addFilters(
		that,
		tableHeadings,
		filtersWrapper,
		levDatatable,
		tableOptions
	) {
		const { default: Lightpick } = await import(
			'lightpick'
		); /* webpackChunkName: "lightpick" */
		const filterableColumns = {},
			filterableColumnNames = {},
			filterableColumnsContent = {},
			filterableColumnsDefault = {},
			filterableIndexes = [];
		tableHeadings.forEach( ( th, index ) => {
			if ( th.dataset.filter === '1' ) {
				filterableIndexes.push( index );
				filterableColumns[ index ] = th.dataset.type ?? 'basic';
				filterableColumnsContent[ index ] = th.dataset.content ?? '';
				filterableColumnsDefault[ index ] =
					th.dataset.defaultfilter ?? '';
				if ( th.dataset.filtername ) {
					filterableColumnNames[ index ] = th.dataset.filtername;
				}
			}
		} );
		const filterTextTesting = document.createElement( 'div' );
		filterTextTesting.id = 'filterTextTesting';

		filterableIndexes.forEach( ( colIndex ) => {
			let filterField,
				rangepickerElement = '',
				filterName = tableHeadings[ colIndex ].innerText;

			if (
				tableHeadings[ colIndex ].dataset.subtype &&
				( tableHeadings[ colIndex ].dataset.subtype === 'start' ||
					tableHeadings[ colIndex ].dataset.subtype === 'end' )
			) {
				filterName = wp.i18n.__( 'date', 'levcharity' );
			}

			if (
				filterableColumnNames[ colIndex ] &&
				filterableColumnNames[ colIndex ] !== ''
			) {
				filterName = filterableColumnNames[ colIndex ];
			}

			const appendTheFilter = true;

			if (
				filterableColumns[ colIndex ] === 'dropdown' ||
				filterableColumns[ colIndex ] === 'split-dropdown'
			) {
				const isSplit =
					filterableColumns[ colIndex ] === 'split-dropdown';

				const filterDefault =
					filterableColumnsDefault[ colIndex ] ?? '';
				const filterClasses = tableHeadings[ colIndex ].dataset.classes;
				let filterAttributes =
					filterClasses && filterClasses.includes( 'multi_choices' )
						? 'multiple="multiple"'
						: '';
				if ( isSplit ) {
					filterAttributes += ' data-split-dropdown="true"';
				}
				filterField = `<div class="dt-filter-field-wrapper">
						<div class="dt-filter-wrapper">
							<label for="${ filterName }-${ colIndex }"></label><select ${ filterAttributes } id="${ filterName }-${ colIndex }" ${
								filterDefault
									? 'data-filterDefault=' + filterDefault
									: ''
							} ${
								filterableColumnsContent[ colIndex ] == 'html'
									? 'data-content="html"'
									: ''
							} class='dt-filter levcharity_select ${
								filterClasses || ''
							}' data-key="${ colIndex }" data-for="${
								document.querySelector( 'table' )?.id
							}">`;

				filterField += `<option value="">${ filterName }</option>`;

				// If the <th> has data-options (JSON array), use those as static options
				// instead of reading from the current page data (which is limited in server-side mode).
				const staticOptionsRaw = tableHeadings[ colIndex ]?.dataset?.options;
				if ( staticOptionsRaw ) {
					try {
						const staticOptions = JSON.parse( staticOptionsRaw );
						staticOptions.forEach( ( val ) => {
							filterField += `<option value="${ val }">${ val }</option>`;
						} );
					} catch ( e ) {}
				} else {
					let columnData = that.api().column( colIndex ).data(),
						filteredColumnData = [];
					for ( let i = 0; i < columnData.length; i++ ) {
						let val = columnData[ i ];
						if ( val && val.indexOf( '<' ) === 0 ) {
							filterTextTesting.innerHTML = val;
							val = filterTextTesting.innerText;
						}
						if ( ! ( filteredColumnData.indexOf( val ) >= 0 ) ) {
							filteredColumnData.push( val );
						}
					}
					filteredColumnData = filteredColumnData.sort(
						function ( a, b ) {
							return new Date( b ) - new Date( a );
						}
					);

					let optionsArray = [];
					filteredColumnData.forEach( ( val ) => {
						if ( isSplit ) {
							val.split( ', ' ).forEach( ( v ) => {
								optionsArray.push( v );
							} );
						} else {
							optionsArray.push( val );
						}
					} );

					optionsArray = optionsArray.filter(
						( value, index, array ) =>
							value !== '' && array.indexOf( value ) === index
					);

					optionsArray.forEach( ( val ) => {
						filterField += `<option value="${ val }">${ val }</option>`;
					} );
				}

				filterField += '</select></div></div>';
			} else if ( filterableColumns[ colIndex ] === 'date' ) {
				const heading = tableHeadings[ colIndex ];
				const filter_key = heading.dataset.key;
				tableOptions.dateFilterKey =
					filter_key && filter_key !== '' ? filter_key : colIndex;

				if ( heading.dataset.subtype ) {
					if ( heading.dataset.subtype === 'start' ) {
						filterField = `<div class="dt-filter-field-wrapper">
							<div class="dt-filter-wrapper orderFilterWrapper form-field">
								<label for="date-min-${ colIndex }">${ wp.i18n.__(
									'Start',
									'levcharity'
								) } ${ filterName }</label>
								<input class='dt-filter' name="min" id="date-min-${ colIndex }" type='date' data-key="${ colIndex }" data-for="${
									document.querySelector( 'table' )?.id
								}"/></label>
							</div>
						</div>`;
					} else if ( heading.dataset.subtype === 'end' ) {
						filterField = `<div class="dt-filter-field-wrapper">
								<div class="dt-filter-wrapper">
									<label for="date-max-${ colIndex }">${ wp.i18n.__(
										'End',
										'levcharity'
									) } ${ filterName }</label>
									<input class='dt-filter' name="max" id="date-max-${ colIndex }" type='date' data-key="${ colIndex }" data-for="${
										document.querySelector( 'table' )?.id
									}"/></label>
								</div>
							</div>`;
					}
				} else {
					filterField = `<div class="dt-filter-field-wrapper">
						<div class="dt-filter-wrapper">
							<label for="date-min-${ colIndex }">${ wp.i18n.__(
								'Start',
								'levcharity'
							) } ${ filterName }</label>
							<input class='dt-filter' name="min" id="date-min-${ colIndex }" type='date' data-key="${ colIndex }" data-for="${
								document.querySelector( 'table' )?.id
							}"/></label>
						</div>
					</div>`;
					filterField += `<div class="dt-filter-field-wrapper">
						<div class="dt-filter-wrapper">
							<label for="date-max-${ colIndex }">${ wp.i18n.__(
								'End',
								'levcharity'
							) } ${ filterName }</label>
							<input class='dt-filter' name="max" id="date-max-${ colIndex }" type='date' data-key="${ colIndex }" data-for="${
								document.querySelector( 'table' )?.id
							}"/></label>
						</div>
					</div>`;
				}
			} else {
				filterField = `<div class="dt-filter-field-wrapper">
					<div class="dt-filter-wrapper form-field">
						<input placeholder="${ filterName }" id="${ filterName }-${ colIndex }" class='dt-filter' type='text' data-key="${ colIndex }" data-for="${
							document.querySelector( 'table' )?.id
						}"/>
					</div>
				</div>`;
			}
			if ( appendTheFilter ) {
				filtersWrapper.innerHTML += `<div class="dt-filter-wrapper filter-type-${ filterableColumns[ colIndex ] }" data-key="${ colIndex }">
					${ filterField }
				</div>`;
			}
			if ( rangepickerElement ) {
				// setTimeout(() => {
				// 	new Lightpick({
				// 		field: document.getElementById(`daterange-${rangepickerElement}`),
				// 		singleDate: false,
				// 		selectForward: true,
				// 		onSelect: function(start, end){
				// 			let dateFilterStart = start ? start.format('YYYY-MM-DD') : '',
				// 				dateFilterEnd = end ? end.format('YYYY-MM-DD') : '';
				//
				// 			if(dateFilterEnd && dateFilterStart && dateFilterStart <= dateFilterEnd) {
				// 				dateFilterEnd = new Date(dateFilterEnd);
				// 				dateFilterStart = new Date(dateFilterStart);
				// 				levDatatable
				// 					.column( index )
				// 					.search(function (value, index, array) {
				// 						var date = new Date(value);
				// 						return date >= dateFilterStart && date <= dateFilterEnd;
				// 					})
				// 					.draw();
				// 			}
				//
				// 		}
				// 	});
				// }, 500);
			}
		} );
	}

	_updateChoices() {
		if ( this._choices.length ) {
			for ( const choice of this._choices ) {
				const choices = [];
				const options = choice?.passedElement?.element?.options || [];

				options.forEach( ( option ) => {
					choices.push( {
						value: option.value,
						label: option.value,
					} );
				} );
				choice.setChoices( choices );
			}
		}
	}

	async initChoices() {
		const selector = '.multi_choices.datatable_multiselect';
		const selects = document.querySelectorAll( selector );
		if ( selects.length ) {
			const { default: Choices } = await import(/* webpackChunkName: "choices" */ 'choices.js');
			let i = 0;
			for ( const select of selects ) {
				if (select.classList.contains('choices__input')) {
					continue;
				}
				const choice = new Choices( select, {
					shouldSortItems: false,
					removeItemButton: true,
				} );

				if (selects.length === 1 && select.closest('.choices')) {
					select.closest('.choices').classList.add('primary');
				}

				const index = +select?.dataset?.key || 0;

				this._choices[ index ] = choice;
				i++;
			}
		}
	}

	_getChoiceValues( index ) {
		const searchValues = [],
			choicesList = this._choices[ index ].itemList.element,
			currentChoices = choicesList?.children || [];

		if ( currentChoices.length ) {
			currentChoices.forEach( ( choice ) => {
				if ( choice.dataset?.value ) {
					searchValues.push( `^${ choice.dataset.value }$` );
				}
			} );
		}
		if ( this.#dataTables ) {
			choicesList.closest(
				'.lev-datatable--actions'
			).style.paddingBottom = choicesList.offsetHeight + 8 + 'px';
			const choicesField = choicesList.closest( '.choices' );

			if (
				choicesField.classList &&
				! choicesField.classList.contains( 'is-flipped' )
			) {
				choicesField.top = choicesField.offsetHeight + 'px';
			}
		}

		return searchValues.length ? searchValues.join( '|' ) : '';
	}

	hideShowpaginationWrapper( levDatatable = null ) {
		const datatables = levDatatable
			? [ levDatatable ]
			: Object.values( this._levDatatables || {} );

		if ( ! datatables.length ) {
			return;
		}

		datatables.forEach( ( datatable ) => {
			if ( ! datatable?.page || ! datatable?.table ) {
				return;
			}

			let info = null;
			try {
				info = datatable.page.info();
			} catch ( error ) {
				info = null;
			}

			if ( ! info ) {
				return;
			}

			const container = datatable.table().container();
			if ( ! container ) {
				return;
			}

			const wrappers = container.querySelectorAll(
				'.lev-datatable--pagination-wrapper'
			);
			const shouldShow = info.pages > 1;

			wrappers.forEach( ( wrapper ) => {
				const infoElement = wrapper.querySelector( '.dt-info' );
				const pagingElement = wrapper.querySelector(
					'.dt-paging.paging_simple_numbers'
				);

				if ( infoElement ) {
					infoElement.style.display = shouldShow ? '' : 'none';
				}

				if ( pagingElement ) {
					pagingElement.style.display = shouldShow ? '' : 'none';
				}
			} );
		} );
	}

	_seedColumnToggleUserState( tableDomId, tableOptions, hiddenColumns ) {
		const state = {};
		tableOptions.columns.forEach( ( column, index ) => {
			if (
				column?.className ===
					'levcharity-datatables-details-control' ||
				tableOptions.headingsTags[ index ].textContent ===
					'Actions' ||
				tableOptions.headingsTags[ index ].textContent === ''
			) {
				return;
			}
			state[ index ] = ! hiddenColumns.some(
				( t ) => Number( t ) === index
			);
		} );
		Datatables.#columnToggleUserState[ tableDomId ] = state;
	}

	_syncLevitColumnToggleCheckboxes( tableDomId ) {
		const host = Datatables.#getLevitActionsHost( tableDomId );
		if ( ! host ) {
			return;
		}
		const state = Datatables.#columnToggleUserState[ tableDomId ];
		if ( ! state ) {
			return;
		}
		const api = Datatables.datatableInstances?.[ tableDomId ];
		host.querySelectorAll( 'input.lev-datatable-column-toggle[data-index]' ).forEach(
			( input ) => {
				const idx = Number.parseInt( input.dataset.index, 10 );
				if ( Number.isNaN( idx ) ) {
					return;
				}
				if (
					! Object.prototype.hasOwnProperty.call( state, idx )
				) {
					return;
				}
				let on = state[ idx ];
				if ( api ) {
					try {
						on = api.column( idx ).visible();
					} catch ( e ) {
						// keep state[idx]
					}
					state[ idx ] = on;
				}
				Datatables.#applyLevitColumnToggleDom( input, on );
			}
		);
	}

	async _addEvents( levDatatable ) {
		const that = this;
		const gsap = ( await import(/* webpackChunkName: "gsap" */ 'gsap' ) ).default;

		this.addDropdownToggle( '.lev-dt-dropdown-toggle', ( dropdown , event) => {
			if(event.target.closest('span.table-column') || event.target.classList.contains('table-column') || dropdown.closest('.datatable-modal')) return;
			let scrollY = 0
			if (window.scrollY !== undefined && window.scrollY !== 0) {
				scrollY = window.scrollY;
			} else if (document.documentElement.scrollTop !== undefined && document.documentElement.scrollTop !== 0) {
				scrollY = document.documentElement.scrollTop;
			} else if (document.body.scrollTop !== undefined) {
				scrollY = document.body.scrollTop;
			} else {
				scrollY = 0;
			}

			let margin_top = -scrollY + 50;
			margin_top = margin_top + 'px';
			dropdown.style.marginTop = margin_top;
		} );

		this.addDocumentClickHandler(
			'.lev-dt-dropdown-toggle',
			'.lev-dt-dropdown'
		);

		DOM.addEvent('change', 'input[name="bulk_action_checkbox[]"]', (event, target) => {
			this.handleBulkSelect('some', target.closest('.datatable_wrapper').querySelector('table').id);
		})
		DOM.addEvent('change', '[id^="bulk_action_all_"]', (event, target) => {
			this.handleBulkSelect('all', target.closest('.datatable_wrapper').querySelector('table').id);
		})
		DOM.addEvent('click', '.dt-paging-button', (event, target) => {
			this.handleBulkSelect('reset', target.closest('.datatable_wrapper').querySelector('table').id);
		})
		DOM.addEvent('change', '#dt-length-0', (event, target) => {
			this.handleBulkSelect('reset', target.closest('.datatable_wrapper').querySelector('table').id);
		})
		DOM.addEvent('change,input', '.lev-datatable--actions *', (event, target) => {
			this.handleBulkSelect('reset', target.closest('.datatable_wrapper').querySelector('table').id);
		})

		DOM.addEvent(
			'change',
			'.lev-datatable--actions .datatable-filters input,.lev-datatable--actions .datatable-filters select,.lev-datatable--actions .datatable-filters textarea',
			( event, filter ) => {
				const index = filter.dataset.key;
				const isSplit = filter?.dataset?.splitDropdown === 'true';

				let searchValue = '';

				try {
					searchValue = levDatatable.column( index ).search();
				} catch ( e ) {}

				const isMultiple = filter.getAttribute( 'multiple' );
				if ( isMultiple && isMultiple === 'multiple' ) {
					const searchValues = that._getChoiceValues( index );

					document.body.click();

					levDatatable
						.column( index )
						.search( searchValues, true, false )
						.draw();
				} else if ( filter.getAttribute( 'type' ) === 'date' ) {
					levDatatable.draw();
				} else if ( searchValue !== filter.value ) {
					let searchValue = filter.value;

					if ( filter.getAttribute( 'data-content' ) !== 'html' ) {
						if ( isSplit ) {
							searchValue = searchValue || '';
							levDatatable
								.column( index )
								.search( searchValue, true, false )
								.draw();
						} else {
							searchValue = searchValue
								? '^' + searchValue + '$'
								: '';
							levDatatable
								.column( index )
								.search( searchValue, true, false )
								.draw();
						}
					} else if (
						filter.getAttribute( 'data-defaultfilter' ) !== ''
					) {
						const filterValue =
							filter.getAttribute( 'data-filterdefault' );
						const select = document.querySelector( '#Status-7' );
						if ( select && select.value == '' ) {
							levDatatable
								.column( 7 )
								.search(
									`^(?!.*${ filterValue }).*$`,
									true,
									false
								)
								.draw();
						} else {
							levDatatable
								.column( index )
								.search( searchValue, true, false )
								.draw();
						}
					} else {
						levDatatable
							.column( index )
							.search( searchValue, true, false )
							.draw();
					}
				}
			}
		);

		const getTransactionData = event => {
			event.preventDefault();
			event.target.classList.add('loading')
			event.target.disabled = true

			const filters = document.querySelectorAll('.reporting-transactions-datatable-wrapper.datatable-transaction-filter input, .datatable-transaction-filter select,  .datatable-transaction-filter textarea');
			let baseUrl = levDatatable.ajax.url().split('?')[0];
			let queryParams = new URLSearchParams();

			filters.forEach((filter) => {
				const name = filter?.name;
				if (name === 'date_range') {
					const value = filter?.value?.trim();
					if(value){
						const [startDate, endDate] = value.split(' - ');
						this._setCookie( 'dateFilterStart', startDate, 1 );
						this._setCookie( 'dateFilterEnd', endDate, 1 );
					}else{
						this._setCookie( 'dateFilterStart', '', 0 );
						this._setCookie( 'dateFilterEnd', '', 0 );
					}
				} else if (filter.type === 'checkbox') {
					if (filter.checked) {
						queryParams.append(name, filter.value);
					}
				} else {
					const value = filter?.value?.trim();
					if (value) {
						queryParams.append(name, value);
					}
				}
			});

			const newUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
			levDatatable.ajax.url(newUrl).load();

			const formData = new FormData();
			formData.append('action', 'get_donations_totals_by_currency');
			formData.append('filters', JSON.stringify(Object.fromEntries(queryParams)));

			fetch( this.#admin_ajax, {
				method: 'POST',
				body: formData,
			})
				.then(response => {
					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}
					const contentType = response.headers.get('content-type');
					if (!contentType || !contentType.includes('application/json')) {
						return response.text().then(text => {
							throw new Error(`Expected JSON but got: ${text.substring(0, 100)}`);
						});
					}
					return response.json();
				})
				.then(data => {
					let amountWrapper = document.querySelector('.transaction_datatable_calculated_results_wrapper');

					if (data.success) {
						let totalsHTML = '<div class="calculated_transaction-results">';
						data.data.forEach(total => {
							totalsHTML += `
                				 <div class="item_${total.currency}">
                    				<span class="currency">${total.currency}</span>
                    				<span class="formatted_totals">${total.formatted_totals}</span>
								 </div>
            				`;
						});
						totalsHTML+= '</div>'

						amountWrapper.innerHTML = totalsHTML;
					} else {
						amountWrapper.innerHTML = `
							<div class="calculated_transaction-results">
								<h4 style="width:100%; text-align:center;">${data.data}</h4>
							</div>
						`
					}

				})
				.catch(error => {
					console.log(error);
				})
				.finally(() => {
					event.target.classList.remove('loading')
					event.target.disabled = false
				})

		}

		document.getElementById('levcharity-reporting-calculate-button')?.addEventListener('click',  getTransactionData);

		document.getElementById('levcharity-reporting-calculate-clear')?.addEventListener('click',  e =>  {
			e.preventDefault();
			const simple_filters = document.querySelectorAll('.reporting-transactions-datatable-wrapper.datatable-transaction-filter input, .datatable-transaction-filter select,  .datatable-transaction-filter textarea');
			const select_filters = document.querySelectorAll('select#levcharity_offline_product_type, select#levcharity_order_product_name')
			simple_filters.forEach((filter) => {
				if(filter.type == 'checkbox'){
					filter.checked = false;
				} else {
					filter.value = '';
				}
			});
			select_filters.forEach(select => {
				try {
					// Check for both property name variations (choices_instance or choicesInstance)
					const choicesInstance = select.choices_instance || select.choicesInstance;
					if (choicesInstance) {
						// Get current value and remove it
						const currentValue = choicesInstance.getValue(true);
						if (currentValue && currentValue.length > 0) {
							// Remove all active items
							if (Array.isArray(currentValue)) {
								currentValue.forEach(value => {
									choicesInstance.removeActiveItemsByValue(value);
								});
							} else {
								choicesInstance.removeActiveItemsByValue(currentValue);
							}
						}
						// Set to empty value
						choicesInstance.setChoiceByValue('');
					}
				}catch(e){
					console.info(`Value changed for already initialized choices select: #${select.id}`, e)
				}
				// Also clear the native select value
				select.value = '';
				select.dispatchEvent(new Event('change'), { bubbles: true })
			})
			getTransactionData(e)
		});

		DOM.addEvent(
			'click',
			'.full-view .action-button:not(.animating)',
			( event, button ) => {
				const dtWrapper = button.closest( '.datatable_wrapper' );

				if ( ! button.classList.contains( 'is-full-view' ) ) {
					const rect = dtWrapper.getBoundingClientRect();
					dtWrapper.style.width = dtWrapper.offsetWidth + 'px';
					dtWrapper.dataset.oldWidth = dtWrapper.offsetWidth;
					dtWrapper.style.position = 'fixed';
					dtWrapper.style.left = rect.left + 'px';
					dtWrapper.style.top = rect.top + 'px';
					dtWrapper.style.zIndex = '9999';
					document.body.style.overflow = 'hidden';
					button.classList.add( 'animating' );

					const tmpDiv = document.createElement( 'div' );
					tmpDiv.dataset.id = 'tmp_' + Helper.uniqueID( 20 );
					dtWrapper.dataset.tmpId = tmpDiv.dataset.id;

					tmpDiv.style.width = dtWrapper.offsetWidth + 'px';
					tmpDiv.style.height = dtWrapper.offsetHeight + 'px';

					DOM.insertAfter( dtWrapper, tmpDiv );

					DOM.el( 'svg path', button ).style.d =
						'path("M5 16h3v3h2v-5H5zm3-8H5v2h5V5H8zm6 11h2v-3h3v-2h-5zm2-11V5h-2v5h5V8z")';

					const adminmenuwrapWidth =
							DOM.el( '#adminmenuwrap' )?.offsetWidth || 0,
						wpadminbarHeight =
							DOM.el( '#wpadminbar' )?.offsetHeight || 0;

					gsap.to( dtWrapper, {
						width: document.body.offsetWidth - adminmenuwrapWidth,
						maxWidth:
							document.body.offsetWidth - adminmenuwrapWidth,
						height: window.innerHeight - wpadminbarHeight,
						maxHeight: window.innerHeight - wpadminbarHeight,
						left: adminmenuwrapWidth,
						top: wpadminbarHeight,
						borderWidth: 0,
						borderRadius: 0,
						duration: 0.3,
						onComplete() {
							dtWrapper.style.overflow = 'auto';
							button.classList.remove( 'animating' );
							button.classList.add( 'is-full-view' );
							dtWrapper.style.height = `calc(100dvh - ${ wpadminbarHeight }px)`;
							dtWrapper.style.maxHeight = `calc(100dvh - ${ wpadminbarHeight }px)`;
							dtWrapper.style.width = `-webkit-fill-available`;
							dtWrapper.style.maxWidth = `calc(100vw - ${ adminmenuwrapWidth }px)`;
						},
					} );
				} else {
					button.classList.add( 'animating' );
					const tmpDiv = DOM.el(
						`[data-id="${ dtWrapper.dataset.tmpId }"]`,
						dtWrapper.parentElement
					);
					dtWrapper.style.overflow = '';
					const rect = tmpDiv.getBoundingClientRect();
					DOM.el( 'svg path', button ).style.d = '';

					dtWrapper.style.height = dtWrapper.offsetHeight + 'px';
					dtWrapper.style.maxHeight = dtWrapper.offsetHeight + 'px';
					dtWrapper.style.width = dtWrapper.offsetWidth + 'px';
					dtWrapper.style.maxWidth = dtWrapper.offsetWidth + 'px';

					gsap.to( dtWrapper, {
						width: dtWrapper.dataset.oldWidth + 'px',
						height: 'auto',
						left: rect.left,
						top: rect.top,
						borderWidth: 1,
						borderRadius: 8,
						duration: 0.3,
						onComplete() {
							dtWrapper.style.position = '';
							dtWrapper.style.zIndex = '';
							dtWrapper.style.top = '';
							dtWrapper.style.left = '';
							dtWrapper.dataset.oldWidth = '';
							button.classList.remove( 'is-full-view' );
							button.classList.remove( 'animating' );

							dtWrapper.style.height = '';
							dtWrapper.style.maxHeight = '';
							dtWrapper.style.width = '';
							dtWrapper.style.maxWidth = '';

							tmpDiv.remove();
							dtWrapper.dataset.tmpId = '';

							document.body.style.overflow = '';
						},
					} );
				}
			}
		);
	}

	handleBulkSelect(action, tableId) {
		const datatableWrapper = DOM.el(`#${tableId}_wrapper`);

		const checkboxes = DOM.els('input[name="bulk_action_checkbox[]"]', datatableWrapper);
		const all = DOM.el(`#bulk_action_all_${tableId}`);
		let hasChecked = false;
		if (action === 'reset') {
			checkboxes.forEach(cb => cb.checked = false);
			if (all) all.checked = false;
		} else if (action === 'all') {
			checkboxes.forEach(cb => {
				cb.checked = all.checked;
			})
			hasChecked = all.checked;
		} else if (action === 'some') {
			let areAllChecked = true;

			checkboxes.forEach(cb => {
				if (!cb.checked) {
					areAllChecked = false;
				}
				if (cb.checked) {
					hasChecked = true;
				}
			});

			if (all) all.checked = areAllChecked;
		}

		const bulkButtons = DOM.els(`.bulk_button_${tableId}`)

		bulkButtons.forEach(el => {
			if (hasChecked) {
				el.classList.remove('hidden')
			} else {
				el.classList.add('hidden')
			}
		})
	}

	handleDocumentClick(event) {
		const button = event.target.closest('.dt_actions_wrapper .actions-dropdown .actions-option');
		if (!button) return;

		this._handleActionsOption(event);
	}

	_handleActionsOption(event) {
		const selectValue = event?.target?.closest('.actions-option');
		const wrapper = event?.target?.closest('.dt_actions_wrapper');

		if (!wrapper.closest('a')) {
			const originalSelect = wrapper?.querySelector('.original_select select');
			if (originalSelect && selectValue?.dataset?.value) {
				originalSelect.value = selectValue.dataset.value;
				originalSelect.dispatchEvent(new Event('change', {
					bubbles: true,
					cancelable: true,
				}));
			}
		}
	}

	addDropdownToggle( toggleSelector, callback = () => {} ) {
		DOM.addEvent( 'click', toggleSelector, ( event, toggle ) => {
			const shown = toggle.classList.contains( 'dropdown-shown' );
			const dropdown = toggle.nextElementSibling;

			const wait = dropdown.classList.contains( 'animation' );

			if ( wait ) {
				return;
			}
			dropdown.classList.add( 'animation' );

			callback( dropdown , event);

			DOM.els( toggleSelector + '.dropdown-shown' ).forEach(
				( _toggle ) => {
					if ( _toggle !== toggle ) {
						const dd = _toggle.nextElementSibling;
						this.#toggleAnimation(
							dd,
							false,
							() => {},
							() => {
								dd.classList.remove( 'shown' );
								_toggle.classList.remove( 'dropdown-shown' );
								dd.classList.remove( 'animation' );
							}
						);
					}
				}
			);

			this.#toggleAnimation(
				dropdown,
				! shown,
				() => {
					dropdown.classList.add( 'shown' );
					toggle.classList.add( 'dropdown-shown' );
					dropdown.classList.remove( 'animation' );
				},
				() => {
					dropdown.classList.remove( 'shown' );
					toggle.classList.remove( 'dropdown-shown' );
					dropdown.classList.remove( 'animation' );
				}
			);
		} );
	}

	addDocumentClickHandler( toggleSelector, dropdownSelector ) {
		DOM.addEvent( 'click', document, ( event ) => {
			if (
				! DOM.compareElement(
					event.target,
					`${ dropdownSelector }, ${ toggleSelector }`
				) ||
				DOM.compareElement(
					event.target,
					`${ dropdownSelector } .cancel-button, ${ dropdownSelector } .actions-option`
				)
			) {
				DOM.els( toggleSelector + '.dropdown-shown' ).forEach(
					( toggle ) => {
						const dropdown = toggle.nextElementSibling;
						const wait = dropdown.classList.contains( 'animation' );

						if ( wait ) {
							return;
						}
						dropdown.classList.add( 'animation' );

						this.#toggleAnimation(
							dropdown,
							false,
							() => {},
							() => {
								dropdown.classList.remove( 'shown' );
								toggle.classList.remove( 'dropdown-shown' );
								dropdown.classList.remove( 'animation' );
							}
						);
					}
				);
			}
		} );
	}

	async #toggleAnimation( element, show, showCallback, hideCallback ) {
		const gsap = ( await import(/* webpackChunkName: "gsap" */ 'gsap' ) ).default;

		if ( ! show ) {
			gsap.to( element, {
				opacity: 0,
				duration: 0.1,
				display: 'none',
				onComplete: hideCallback,
			} );
		} else {
			gsap.to( element, {
				opacity: 1,
				duration: 0.1,
				display: '',
				onComplete: showCallback,
			} );
		}
	}

	_datatableActions() {
		document.addEventListener( 'change', async ( event ) => {
			if ( event.target.matches( '.select_actions_wrapper' ) ) {
				if ( event.target.selectedOptions[ 0 ].dataset.url ) {
					if (
						event.target.selectedOptions[ 0 ].dataset.newtab ===
						'true'
					) {
						window.open(
							event.target.selectedOptions[ 0 ].dataset.url,
							'_blank'
						);
						event.target.value = '';
					} else {
						window.location.href =
							event.target.selectedOptions[ 0 ].dataset.url;
					}
				}

				if (
					event.target.selectedOptions[ 0 ].dataset.confdelete ===
					'true'
				) {
					const { Components } = await import(
						'../helpers/Components.js' /* webpackChunkName: "components" */
					);
					const deletion_title =
						event.target.selectedOptions[ 0 ].dataset.title;
					const modal = Components.modals();
					modal
						.type( 'popup' )
						.className( 'html-modal' )
						.closeOnOutsideClick( false )
						.header(
							`<h1 class="levcharity_heading">${ deletion_title }</h1>`,
							true
						).body( `
						 <div class="levcharity_popup_html delete_pop_up">
						   <a class="levcharity_button primary primary_button levcharity_modal_close_button" href="${ event.target.selectedOptions[ 0 ].dataset.urls }">Delete</a>
						   <button class="levcharity_button levcharity_modal_close_button levcharity_popup_modal_close_button levcharity_modal_close_button_xmark levcharity_popup_modal_close_button_xmar">Cancel</button>
						 </div>
					   	` );
					modal.show();
				}
			}
		} );
	}
	addScrollForDropdown() {

		const getDropdowns = () => document.querySelectorAll('.actions-dropdown');

		let dropdowns = getDropdowns();

		const updateDropdowns = (type) => {

			dropdowns = getDropdowns();
			if (dropdowns.length === 0) {
				return;
			}

			const modal = document.querySelector('.datatable-modal .levcharity_fetch_datatable');
			let scrollY;

			if (window.scrollY !== undefined && window.scrollY !== 0) {
				scrollY = window.scrollY;
			} else if (document.documentElement.scrollTop !== undefined && document.documentElement.scrollTop !== 0) {
				scrollY = document.documentElement.scrollTop;
			} else if (document.body.scrollTop !== undefined) {
				scrollY = document.body.scrollTop;
			} else {
				scrollY = 0;
			}

			if (modal) {
				scrollY = modal.scrollTop;
				let margin_top = -scrollY + 50 + 'px';
				dropdowns.forEach((dropdown) => {
					if(dropdown){
						dropdown.style.marginTop = margin_top;
					}
				});
			}

			let margin_top = -scrollY + 50 + 'px';
			dropdowns.forEach((dropdown) => {
				if(dropdown && !dropdown.closest('.datatable-modal')){
					dropdown.style.marginTop = margin_top;
				}
			});
		};


		window.addEventListener('scroll', updateDropdowns);
		window.addEventListener('resize', updateDropdowns);

		document.body.addEventListener('scroll', updateDropdowns);

		const modal = document.querySelector('.datatable-modal .levcharity_fetch_datatable');
		if (modal) {
			modal.addEventListener('scroll', updateDropdowns);
		}
	}

	addExistingFilters( table, levDatatable ) {
		setTimeout( () => {
			if ( table && levDatatable ) {
				const table_id = table.getAttribute( 'id' );
				const existingFiltersElements = document.querySelectorAll(
					`[data-for="${ table_id }"]`
				);
				if ( existingFiltersElements ) {
					Array.from( existingFiltersElements ).forEach(
						( element, index ) => {
							const elementTagName =
								element.tagName.toLowerCase();
							if ( elementTagName === 'select' ) {
								element.addEventListener(
									'change',
									( event ) => {
										const name =
											event.target.getAttribute( 'name' );
										const data_table = levDatatable;
										const settings =
											data_table.settings()[ 0 ];
										const ajax = settings.ajax;
										ajax.data[ name ] = event.target.value;
										levDatatable.ajax.reload();
									}
								);
							}
							if ( elementTagName === 'input' ) {
								let timeout = null;
								element.addEventListener(
									'input',
									( event ) => {
										clearTimeout( timeout );

										timeout = setTimeout( () => {
											const name =
												event.target.getAttribute(
													'name'
												);
											const data_table = levDatatable;
											const settings =
												data_table.settings()[ 0 ];
											const ajax = settings.ajax;
											ajax.data[ name ] =
												event.target.value;
											levDatatable.ajax.reload();
										}, 500 );
									}
								);
							}
						}
					);
				}
			}
		}, 3000 );
	}

	#renderChildRow( data ) {
		data = data?.child_row || [];

		let content = '';
		const columns = [];
		let headings = [];

		if ( Array.isArray( data ) ) {
			headings = Object.keys( data[ 0 ] );

			for ( const i in data ) {
				for ( const j in Object.values( data[ i ] ) ) {
					const item = Object.values( data[ i ] )[ j ];
					if ( typeof columns[ j ] !== 'undefined' ) {
						columns[ j ].push( item );
					} else {
						columns.push( [ item ] );
					}
				}
			}
		}
		for ( const i in columns ) {
			let column = columns[ i ].map(
				( cell ) => `<span class="cell">${ cell }</span>`
			);

			if ( typeof headings[ i ] !== 'undefined' ) {
				column = [
					`<span class="cell heading">${ headings[ i ] }</span>`,
					...column,
				];
			}

			content += `<div class="column">${ column.join( '' ) }</div>`;
		}

		return `<div class="levcharity_datatable_child_row">${ content }</div>`;
	}
	handleSuccessReload(event, tableID){
		const response = event.detail;
		if(response.success){
			if (tableID && Datatables.datatableInstances?.[tableID]) {
				Datatables.datatableInstances[tableID].ajax.reload();
			}
		}
	}

	#getFilteredOptions(tableID, tableOptions) {
		const options = [];

		if (tableID === 'levcharity-reporting-donations-table') {
			const canExport = this.#current_user_can?.includes('export_levcharity_transactions');
			const canImport = this.#current_user_can?.includes('import_levcharity_transactions');

			if (canExport) {
				options.push(...tableOptions.buttons);
			}
			if (canImport && tableOptions?.additionalActions?.length) {
				options.push(...tableOptions.additionalActions);
			}
		} else {
			options.push(...tableOptions.buttons);
			if (tableOptions?.additionalActions?.length) {
				tableOptions.additionalActions.forEach(action => options.push(action));
			}
		}

		return options;
	}

	#checkTablePermission(tableId) {
		const permissions = {
			'levcharity-donations-table': 'export_levcharity_donations',
			'levcharity-donation-equipment-table': 'export_levcharity_donation_equipment',
			'levcharity-ecards-table': 'export_levcharity_ecards',
			'levcharity-ecards-log-table': 'export_levcharity_ecard_log',
			'levcharity-campaigns-table': 'export_levcharity_campaigns',
			'lev-charity-p2p-campaigns-table': 'export_levcharity_p2p_campaigns',
			'levcharity-team-campaigns-table': 'export_levcharity_team_campaigns',
			'levcharity-events-table': 'export_levcharity_events',
			'levcharity-events-attendees-table': 'export_levcharity_events_attendees',
			'levcharity-my-events-table': 'export_levcharity_events',
			'lev-charity-my-account-events-attendees-table_processing': 'export_levcharity_events_attendees',
			'levcharity-digital-ads-table': 'export_levcharity_digital_ads',
			'levcharity-reporting-donations-table': ['export_levcharity_transactions', 'import_levcharity_transactions'],
			'lev-charity-crm-users-table': 'export_levcharity_contacts',
			'levcharity-reporting-recurrence-table': 'export_levcharity_recurring',
			'levcharity-salesforce-warning-table': 'export_levcharity_salesforce',
			'levcharity-salesforce-error-table': 'export_levcharity_salesforce',
			'levcharity-emails-donations-table': 'export_levcharity_emails',
			'levcharity-emails-ecards-table': 'export_levcharity_emails',
			'levcharity-emails-p2p-campaigns-table': 'export_levcharity_emails',
			'levcharity-emails-teams-table': 'export_levcharity_emails',
			'levcharity-emails-subscriptions-table': 'export_levcharity_emails',
			'levcharity-emails-donations-equipment-table': 'export_levcharity_emails',
			'levcharity-emails-campaigns-table': 'export_levcharity_emails',
			'levcharity-emails-team-campaigns-table': 'export_levcharity_emails',
			'levcharity-emails-events-table': 'export_levcharity_emails',
			'levcharity-emails-users-table': 'export_levcharity_emails',
			'levcharity-emails-log-table': 'export_levcharity_email_log',
			'levcharity-receipts-table': 'export_levcharity_receipts',
			'lev-charity-user-tags-table': 'export_levcharity_user_tags',
			'lev-charity-team-campaign-donors-table': 'export_levcharity_team_campaigns',
			'levcharity-team-campaigns-teams-table': 'export_levcharity_team_campaigns',
			'levcharity-team-campaigns-users-table': 'export_levcharity_team_campaigns',
		};

		const permission = permissions[tableId];
		if (Array.isArray(permission)) {
			return permission.some(p => this.#current_user_can?.includes(p));
		}
		return this.#current_user_can?.includes(permission) ?? false;
	}
}
